import * as React from 'react';
import { useApi } from '../../../design-system/useApi';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { VISUAL } from '../../../design-system/visual';
import type { LeaveBalance } from '../types';

interface Props {
  employeeId: string;
  year?: number;
  /** Optional heading; omit to render bare cards. */
  heading?: string;
}

/** Per-type allocated / used / remaining cards for one employee + year. */
export function LeaveBalances({ employeeId, year, heading }: Props) {
  const y = year ?? new Date().getUTCFullYear();
  const { data, loading, error, status } = useApi<{ balances: LeaveBalance[] }>(
    `/api/hr/employees/${employeeId}/leave-balances?year=${y}`,
    [employeeId, y],
  );

  if (loading) return <LoadingState variant="inline" />;
  // Honest error branch — a 403/failed load previously fell through to the
  // "No tracked leave types configured" copy, which reads as a config choice.
  if (status === 403) {
    return <p className="text-[12.5px] text-muted-foreground">You don't have access to this employee's leave balances.</p>;
  }
  if (error) {
    return <p className="text-[12.5px] text-destructive">Couldn't load leave balances: {error}</p>;
  }
  const balances = data?.balances ?? [];
  if (balances.length === 0) {
    return <p className="text-[12.5px] text-muted-foreground">No tracked leave types configured.</p>;
  }

  return (
    <div className="space-y-3">
      {heading && <h3 className={VISUAL.dashSection}>{heading}</h3>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {balances.map((b) => {
          const allocated = Number(b.allocated) + Number(b.carried_over);
          const used = Number(b.used);
          const remaining = b.remaining;
          const pct = allocated > 0 ? Math.min(100, Math.round((used / allocated) * 100)) : 0;
          const tracked = allocated > 0;
          return (
            <div key={b.leave_type_id} className={[VISUAL.bento, 'p-4'].join(' ')}>
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ background: b.color ?? 'var(--saffron, oklch(0.78 0.13 70))' }}
                  aria-hidden
                />
                <p className="text-[12.5px] font-semibold text-foreground truncate">{b.type_name}</p>
              </div>
              {tracked ? (
                <>
                  <p className="font-sans font-light text-[1.8rem] leading-none tracking-[-0.03em] tabular-nums text-foreground mt-2.5">
                    {remaining}
                    <span className="text-[12px] text-muted-foreground font-normal"> / {allocated} left</span>
                  </p>
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-saffron" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">{used} day{used === 1 ? '' : 's'} used</p>
                </>
              ) : (
                <p className="text-[12px] text-muted-foreground mt-2.5">Untracked{b.is_paid ? '' : ' · unpaid'}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
